import React, { useMemo } from 'react'
import { startCase } from 'lodash-es'
import { Avatar, Box, ComboboxItem, Text, Tooltip } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import {
  BeneficiaryDetails,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptionsLabelMapping,
} from 'types/beneficiaries'
import { getTransferDestinations } from 'utils/beneficiaries'
import { isValidEntityTypeAndTransferDestination } from '../utils/payFormUtils'

interface UseBeneficiaryOptions {
  beneficiaries: BeneficiaryDetails[]
  walletTransferDestinations: BeneficiaryTransferDestination[]
  isB2cPayoutEnabled?: boolean
}

const useBeneficiaryOptions = ({
  beneficiaries,
  walletTransferDestinations,
  isB2cPayoutEnabled,
}: UseBeneficiaryOptions) => {
  return useMemo(() => {
    const transferDestinations = getTransferDestinations(walletTransferDestinations)

    const filteredBeneficiaries = beneficiaries?.filter(beneficiary => {
      return isValidEntityTypeAndTransferDestination(
        beneficiary?.transferDestination,
        beneficiary?.entityType,
        transferDestinations,
        isB2cPayoutEnabled
      )
    })

    const items: ComboboxItem[] = filteredBeneficiaries?.map(beneficiary => {
      const label =
        beneficiary?.businessName || `${beneficiary?.firstName} ${beneficiary?.lastName}`
      const aliasLabel = beneficiary?.alias ? `(${beneficiary.alias})` : ''

      const paymentMethodLabel =
        !!beneficiary?.paymentMethod &&
        USDBeneficiaryOptionsLabelMapping[beneficiary?.paymentMethod]
          ? `| ${USDBeneficiaryOptionsLabelMapping[beneficiary?.paymentMethod]}`
          : ''

      return {
        LeftElement: <Avatar size="small" type="currency" name={beneficiary?.currency} />,
        leftText: `${label} ${aliasLabel}`,
        rightText: startCase(beneficiary?.entityType.toLowerCase()),
        secondaryText: (
          <Tooltip position="top" bodyContent={beneficiary?.fiat?.accountNumber} type="simple">
            <Box onMouseEnter={e => e.stopPropagation()} onMouseLeave={e => e.stopPropagation()}>
              <Text variant="body-text">
                {`${ellipseMiddleText(beneficiary?.fiat?.accountNumber, 6, 6)} | ${beneficiary?.fiat?.code} ${paymentMethodLabel}`}
              </Text>
            </Box>
          </Tooltip>
        ),
        value: beneficiary?.reference,
      }
    })

    return items
  }, [beneficiaries, walletTransferDestinations, isB2cPayoutEnabled])
}

export default useBeneficiaryOptions
